import { Button, Card, MessageBar, MessageBarBody, MessageBarTitle, makeStyles, tokens, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from '@fluentui/react-components';
import { StarFilled, StarRegular } from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Offer, OfferLine } from '../../../models/Offer';
import { BillingCycle } from '../../../models/enums';
import { createId } from '../../../services/dataverse/dataverseClient';
import { useCatalog } from '../../../hooks/useCatalog';
import { useClients } from '../../../hooks/useClients';
import { useExchangeRate } from '../../../hooks/useExchangeRate';
import { useOffers } from '../../../hooks/useOffers';
import { useFavorites } from '../../../hooks/useFavorites';
import { calculateOfferLineTotal } from '../../../utils/tax';
import { PageHeader } from '../../common/PageHeader';
import { LoadingState } from '../../common/LoadingState';
import { ErrorState } from '../../common/ErrorState';
import { OfferHeader } from './OfferHeader';
import { OfferLines } from './OfferLines';
import { OfferSummary } from './OfferSummary';
import { ProductPicker } from './ProductPicker';
import { OfferPdfPreview } from './OfferPdfPreview';
import { SendOfferDialog } from './SendOfferDialog';

const useStyles = makeStyles({
  layout: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: tokens.spacingHorizontalL },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, alignSelf: 'flex-start', position: 'sticky' as const, top: tokens.spacingVerticalM },
});

export const OfferBuilderPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const { clients, loading: clientsLoading, error: clientsError } = useClients();
  const { catalogItems, loading: catalogLoading, error: catalogError } = useCatalog();
  const { rates, currentRate, loading: ratesLoading, error: ratesError } = useExchangeRate();
  const { getOffer, createDraftTemplate, saveOffer, sendOffer, getPreviewHtml } = useOffers();
  const { favorites, isFavorite } = useFavorites();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [lines, setLines] = useState<OfferLine[]>([]);
  const [selectedSku, setSelectedSku] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      setError(null);
      try {
        if (id) {
          const existing = await getOffer(id);
          if (!existing) throw new Error('Offer not found.');
          setOffer(existing);
          setLines(existing.lines ?? []);
        } else if (currentRate) {
          const draft = await createDraftTemplate(undefined, currentRate.cr_crm_rate, 0.19);
          draft.cr_crm_exchangerateid = currentRate.cr_crm_exchangerateid;
          setOffer(draft);
          setLines(draft.lines);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to initialize offer builder.');
      } finally {
        setLoading(false);
      }
    };
    void initialize();
  }, [createDraftTemplate, currentRate, getOffer, id]);

  const pageError = error ?? clientsError ?? catalogError ?? ratesError;
  const isBusy = loading || clientsLoading || catalogLoading || ratesLoading;

  const addProduct = async () => {
    if (!offer) return;
    const product = catalogItems.find((item) => item.cr_crm_catalogcacheid === selectedSku);
    if (!product) return;

    const priceUsd = product.cr_crm_erppriceusd > 0 ? product.cr_crm_erppriceusd : product.cr_crm_unitpriceusd;

    const line: OfferLine = {
      cr_crm_offerlineid: createId('offerline'),
      cr_crm_offerid: offer.cr_crm_offerid,
      cr_crm_skuid: product.cr_crm_skuid,
      cr_crm_skuname: product.cr_crm_skuname,
      cr_crm_productname: product.cr_crm_productname,
      cr_crm_quantity: 1,
      cr_crm_unitpriceusd: priceUsd,
      cr_crm_unitpricetnd: Number((priceUsd * offer.cr_crm_exchangerateval).toFixed(3)),
      cr_crm_discountpct: 0,
      cr_crm_linetotaltnd: calculateOfferLineTotal(1, priceUsd, offer.cr_crm_exchangerateval, 0),
      cr_crm_billingcycle: product.cr_crm_billingcycles as unknown as BillingCycle,
      cr_crm_sortorder: lines.length + 1,
    };
    setLines((current) => [...current, line]);
    setSelectedSku('');
  };

  const addProductFromCatalogId = (catalogId: string) => {
    if (!offer) return;
    const product = catalogItems.find((item) => item.cr_crm_catalogcacheid === catalogId);
    if (!product) return;
    const priceUsd = product.cr_crm_erppriceusd > 0 ? product.cr_crm_erppriceusd : product.cr_crm_unitpriceusd;
    const line: OfferLine = {
      cr_crm_offerlineid: createId('offerline'),
      cr_crm_offerid: offer.cr_crm_offerid,
      cr_crm_skuid: product.cr_crm_skuid,
      cr_crm_skuname: product.cr_crm_skuname,
      cr_crm_productname: product.cr_crm_productname,
      cr_crm_quantity: 1,
      cr_crm_unitpriceusd: priceUsd,
      cr_crm_unitpricetnd: Number((priceUsd * offer.cr_crm_exchangerateval).toFixed(3)),
      cr_crm_discountpct: 0,
      cr_crm_linetotaltnd: calculateOfferLineTotal(1, priceUsd, offer.cr_crm_exchangerateval, 0),
      cr_crm_billingcycle: product.cr_crm_billingcycles as unknown as BillingCycle,
      cr_crm_sortorder: lines.length + 1,
    };
    setLines((current) => [...current, line]);
  };

  const favoriteItems = useMemo(
    () => catalogItems.filter((item) => isFavorite(item.cr_crm_catalogcacheid)),
    [catalogItems, favorites, isFavorite],
  );

  const updateLine = (lineId: string, patch: Partial<OfferLine>) => {
    if (!offer) return;
    setLines((current) => current.map((line) => line.cr_crm_offerlineid === lineId ? {
      ...line,
      ...patch,
      cr_crm_linetotaltnd: calculateOfferLineTotal(
        patch.cr_crm_quantity ?? line.cr_crm_quantity,
        patch.cr_crm_unitpriceusd ?? line.cr_crm_unitpriceusd,
        offer.cr_crm_exchangerateval,
        patch.cr_crm_discountpct ?? line.cr_crm_discountpct,
      ),
    } : line));
  };

  const removeLine = (lineId: string) => setLines((current) => current.filter((line) => line.cr_crm_offerlineid !== lineId));

  const handleOfferChange = (updated: Offer) => {
    const prevRate = offer?.cr_crm_exchangerateval;
    setOffer(updated);
    // Recalculate all line totals when exchange rate changes
    if (prevRate !== undefined && updated.cr_crm_exchangerateval !== prevRate) {
      setLines((current) => current.map((line) => ({
        ...line,
        cr_crm_unitpricetnd: Number((line.cr_crm_unitpriceusd * updated.cr_crm_exchangerateval).toFixed(3)),
        cr_crm_linetotaltnd: calculateOfferLineTotal(
          line.cr_crm_quantity,
          line.cr_crm_unitpriceusd,
          updated.cr_crm_exchangerateval,
          line.cr_crm_discountpct,
        ),
      })));
    }
  };

  const defaultEmail = useMemo(() => clients.find((item) => item.cr_crm_clientid === offer?.cr_crm_clientid)?.cr_crm_email ?? '', [clients, offer?.cr_crm_clientid]);

  if (isBusy) return <LoadingState label="Loading offer builder..." />;
  if (pageError || !offer) return <ErrorState message={pageError ?? 'Offer data unavailable.'} onRetry={() => navigate('/offers')} />;

  return (
    <div>
      <PageHeader title={id ? `Edit ${offer.cr_crm_reference}` : 'Create offer'} subtitle="Compose pricing lines, tax and exchange rate for a commercial proposal" actions={[{ label: 'Back to offers', onClick: () => navigate('/offers') }]} />
      <div className={styles.layout}>
        <div className={styles.stack}>
          <Card>
            <OfferHeader offer={offer} clients={clients} exchangeRates={rates} onChange={handleOfferChange} />
          </Card>
          <Card style={{ overflow: 'visible' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1, overflow: 'visible' }}>
                <ProductPicker items={catalogItems} selectedSku={selectedSku} onSelect={setSelectedSku} onAdd={addProduct} />
              </div>
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button
                    icon={favoriteItems.length > 0 ? <StarFilled style={{ color: '#f59e0b' }} /> : <StarRegular />}
                    appearance="subtle"
                    title="Add a favorite product"
                    disabled={favoriteItems.length === 0 || !offer}
                  >
                    Favorites{favoriteItems.length > 0 ? ` (${favoriteItems.length})` : ''}
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    {favoriteItems.length === 0 ? (
                      <MenuItem disabled>No favorites saved yet</MenuItem>
                    ) : (
                      favoriteItems.map((item) => (
                        <MenuItem
                          key={item.cr_crm_catalogcacheid}
                          onClick={() => addProductFromCatalogId(item.cr_crm_catalogcacheid)}
                        >
                          {item.cr_crm_productname} — {item.cr_crm_skuname}
                          <span style={{ marginLeft: 8, fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                            SKU: {item.cr_crm_skuid}
                          </span>
                        </MenuItem>
                      ))
                    )}
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          </Card>
          <Card>
            <OfferLines lines={lines} onUpdate={updateLine} onRemove={removeLine} />
          </Card>
        </div>
        <div className={styles.stack}>
          <OfferSummary lines={lines} taxRate={offer.cr_crm_taxrate} />
          {lines.length === 0 ? (
            <MessageBar intent="warning"><MessageBarBody><MessageBarTitle>Offer is empty</MessageBarTitle>Add at least one line before saving.</MessageBarBody></MessageBar>
          ) : null}
          <Button appearance="primary" onClick={async () => {
            if (!offer.cr_crm_clientid) { setSaveMsg('❌ Please select a client first'); return; }
            if (lines.length === 0) { setSaveMsg('❌ Add at least one product line'); return; }
            try {
              setSaveMsg('Saving...');
              await saveOffer(offer, lines);
              setSaveMsg('✅ Draft saved!');
            } catch (err) {
              setSaveMsg(`❌ ${err instanceof Error ? err.message : 'Save failed'}`);
            }
          }}>Save draft</Button>
          {saveMsg && <span style={{ fontSize: 13 }}>{saveMsg}</span>}
          <Button onClick={async () => {
            // Enrich offer with full client data for preview
            const client = clients.find(c => c.cr_crm_clientid === offer.cr_crm_clientid);
            const enrichedOffer = client ? { ...offer, cr_crm_client: {
              cr_crm_companyname: client.cr_crm_companyname,
              cr_crm_address: client.cr_crm_address,
              cr_crm_city: client.cr_crm_city,
              cr_crm_postalcode: client.cr_crm_postalcode,
              cr_crm_country: client.cr_crm_country,
              cr_crm_email: client.cr_crm_email,
              cr_crm_phone: client.cr_crm_phone,
              cr_crm_vatnumber: client.cr_crm_vatnumber,
            }} : offer;
            setPreviewHtml(await getPreviewHtml(enrichedOffer, lines));
            setPreviewOpen(true);
          }}>Preview PDF</Button>
          <Button onClick={async () => {
            if (!offer.cr_crm_clientid || lines.length === 0) { setSaveMsg('❌ Select a client and add lines first'); return; }
            try {
              setSaveMsg('Saving & generating PDF...');
              await saveOffer(offer, lines);
              const client = clients.find(c => c.cr_crm_clientid === offer.cr_crm_clientid);
              const enrichedOffer = client ? { ...offer, cr_crm_client: {
                cr_crm_companyname: client.cr_crm_companyname,
                cr_crm_address: client.cr_crm_address,
                cr_crm_city: client.cr_crm_city,
                cr_crm_postalcode: client.cr_crm_postalcode,
                cr_crm_country: client.cr_crm_country,
                cr_crm_email: client.cr_crm_email,
                cr_crm_phone: client.cr_crm_phone,
                cr_crm_vatnumber: client.cr_crm_vatnumber,
              }} : offer;
              const html = await getPreviewHtml(enrichedOffer, lines);
              const printWindow = window.open('', '_blank');
              if (printWindow) {
                printWindow.document.write(html);
                printWindow.document.close();
                printWindow.onload = () => { printWindow.print(); };
              }
              setSaveMsg('');
            } catch (err) {
              setSaveMsg(`❌ ${err instanceof Error ? err.message : 'PDF generation failed'}`);
            }
          }}>Download PDF</Button>
          <Button onClick={() => setSendOpen(true)} disabled={!offer.cr_crm_clientid || lines.length === 0}>Send offer</Button>
        </div>
      </div>
      <OfferPdfPreview open={previewOpen} html={previewHtml} onOpenChange={setPreviewOpen} />
      <SendOfferDialog open={sendOpen} defaultEmail={defaultEmail} onOpenChange={setSendOpen} onSend={(email, message, cc) => { void sendOffer(offer.cr_crm_offerid, email, message, cc); setSendOpen(false); }} />
    </div>
  );
};
